/**
 * Datadog RUM helpers for Poker generosity flows.
 *
 * Covers:
 *  - Playtech enrollment check (start / result / error)
 *  - Playtech enrollment gate shown / CTA clicked
 *  - Poker tournament ticket launched via casino launcher
 *  - Poker tournament ticket clicked (direct URL / desktop client)
 *  - Poker chunked bonus viewed / error
 */

import type { TRumAction, TRumError } from './types';

// ---------------------------------------------------------------------------
// Playtech enrollment
// ---------------------------------------------------------------------------

/** Fires when a cache miss triggers an API enrollment check. */
export const ddActionPokerEnrollmentStarted = (context: { region: string }): TRumAction => ({
    name: 'POKER_ENROLLMENT_CHECK_STARTED',
    description: 'Playtech enrollment API check started.',
    use_case: 'PROMOTIONPAGE',
    context
});

/** Fires when the enrollment check resolves (API or cache). */
export const ddActionPokerEnrollmentResult = (context: {
    enrolled: boolean;
    region: string;
    cached: boolean;
    userId: string;
}): TRumAction => ({
    name: 'POKER_ENROLLMENT_RESULT',
    description: context.enrolled ? 'User is enrolled in Playtech poker.' : 'User is not enrolled in Playtech poker.',
    use_case: 'PROMOTIONPAGE',
    context
});

/** Fires when the enrollment API throws — user is failed open to enrolled. */
export const ddErrorPokerEnrollmentFailed = (context: { region: string }): TRumError => ({
    name: 'POKER_ENROLLMENT_CHECK_FAILED',
    description: 'Playtech enrollment check failed — failing open to enrolled.',
    use_case: 'PROMOTIONPAGE',
    context
});

// ---------------------------------------------------------------------------
// Enrollment gate
// ---------------------------------------------------------------------------

/** Fires once when the not-enrolled gate card is shown to the user. */
export const ddActionPokerGateShown = (context: { region: string; userId: string }): TRumAction => ({
    name: 'POKER_GATE_SHOWN',
    description: 'Playtech enrollment gate shown to user.',
    use_case: 'PROMOTIONPAGE',
    context
});

/** Fires when the user clicks "Open Poker Lobby" from the enrollment gate. */
export const ddActionPokerGateCtaClicked = (): TRumAction => ({
    name: 'POKER_GATE_CTA_CLICKED',
    description: 'User clicked Open Poker Lobby from the Playtech enrollment gate.',
    use_case: 'GAME_LAUNCHER'
});

// ---------------------------------------------------------------------------
// Game config fallback
// ---------------------------------------------------------------------------

/** Fires when no Playtech game is found in the lobby zones and the fallback config is used. */
export const ddErrorPokerGameConfigFallback = (context: { reason: 'no_zones' | 'no_playtech_game' }): TRumError => ({
    name: 'POKER_GAME_CONFIG_FALLBACK',
    description:
        context.reason === 'no_zones'
            ? 'Lobby page config returned no zones — using fallback Playtech game config.'
            : 'No Playtech game found in lobby zones — using fallback Playtech game config.',
    use_case: 'GAME_LAUNCHER',
    context
});

// ---------------------------------------------------------------------------
// Tournament ticket launch
// ---------------------------------------------------------------------------

/** Fires when a ticket is launched via the casino launcher (PokerTournamentTicketGameLauncherWrapper path). */
export const ddActionPokerTournamentTicketLaunched = (context: { url: string }): TRumAction => ({
    name: 'POKER_TOURNAMENT_TICKET_LAUNCHED',
    description: 'Poker tournament ticket game launched via casino launcher.',
    use_case: 'GAME_LAUNCHER',
    context
});
